import { IRouter, IRouterEvents, type RouteConfig, route } from '@aurelia/router';
import { IDisposable, resolve } from 'aurelia';

import { loadFromRoot, navigateFromRoot } from './lib/navigation';
import { OPEN_TEMPLATE_LIBRARY_STORAGE_KEY } from './lib/ui-flags';
import { AiohaService, type WalletProvider } from './services/aioha-service';
import { ApiClient } from './services/api-client';

const OPEN_LOGIN_MODAL_EVENT = 'hivett-open-login-modal';
const CREDIT_BALANCE_REFRESH_MS = 30_000;
const THEME_STORAGE_KEY = 'hivett-theme';

type ShellNavItem = {
  id: string;
  label: string;
  path: string;
  matches: (routeId: string) => boolean;
};

type ShellCommandItem = {
  id: string;
  title: string;
  description: string;
  group: string;
  keywords: string;
  kind: 'route' | 'action' | 'automation';
  path?: string;
  actionId?: 'open-login' | 'toggle-theme' | 'new-automation' | 'open-templates' | 'logout';
  automationId?: string;
};

type ShellCommandResult = ShellCommandItem & { index: number };

type CommandAutomation = {
  id: string;
  name: string;
  enabled: boolean;
  triggerType: string | undefined;
  account: string | undefined;
};

const APP_NAV_ITEMS: ShellNavItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    path: '/overview',
    matches: (routeId) => routeId === 'overview'
  },
  {
    id: 'automations',
    label: 'Automations',
    path: '/automations',
    matches: (routeId) => routeId === 'automations' || routeId === 'automations-edit'
  },
  {
    id: 'runs',
    label: 'Runs',
    path: '/runs',
    matches: (routeId) => routeId === 'runs' || routeId === 'failed-runs' || routeId === 'run-detail'
  },
  {
    id: 'audit',
    label: 'Audit',
    path: '/audit',
    matches: (routeId) => routeId === 'audit'
  },
  {
    id: 'queues',
    label: 'Queues',
    path: '/queues',
    matches: (routeId) => routeId === 'queues'
  },
  {
    id: 'billing',
    label: 'Billing',
    path: '/billing',
    matches: (routeId) => routeId === 'billing'
  }
];

const ROUTE_LABELS: Record<string, string> = {
  overview: 'Overview',
  automations: 'Automations',
  'automations-edit': 'Editor',
  runs: 'Runs',
  'failed-runs': 'Failed Runs',
  'run-detail': 'Run',
  audit: 'Audit',
  queues: 'Queues',
  billing: 'Billing',
  settings: 'Settings',
  login: 'Sign in'
};

@route({
  routes: [
    {
      id: 'login',
      path: ['', 'login'],
      component: import('./routes/login/login')
    },
    {
      id: 'auth-callback',
      path: 'auth/callback',
      component: import('./routes/auth-callback/auth-callback')
    },
    {
      id: 'overview',
      path: 'overview',
      component: import('./routes/overview/overview')
    },
    {
      id: 'automations',
      path: 'automations',
      component: import('./routes/automations/automations')
    },
    {
      id: 'automations-edit',
      path: [
        'automation/:id',
        'automation/:id/template/:template',
      ],
      component: import('./routes/automation-edit/automation-edit')
    },
    {
      id: 'runs',
      path: 'runs',
      component: import('./routes/runs/runs')
    },
    {
      id: 'failed-runs',
      path: 'runs/failed',
      component: import('./routes/runs/failed-runs')
    },
    {
      id: 'run-detail',
      path: 'run/:id',
      component: import('./routes/runs/run-detail')
    },
    {
      id: 'billing',
      path: 'billing',
      component: import('./routes/billing/billing')
    },
    {
      id: 'audit',
      path: 'audit',
      component: import('./routes/audit/audit')
    },
    {
      id: 'queues',
      path: 'queues',
      component: import('./routes/queues/queues')
    },
    {
      id: 'settings',
      path: ['settings', 'settings/keys'],
      component: import('./routes/settings/settings')
    },
    {
      id: 'about',
      path: 'about',
      component: import('./routes/about/about')
    },
    {
      id: 'terms',
      path: 'terms',
      component: import('./routes/terms/terms')
    },
    {
      id: 'privacy',
      path: 'privacy',
      component: import('./routes/privacy/privacy')
    },
    {
      id: 'not-found',
      path: '*notFound',
      component: import('./routes/not-found/not-found')
    }
  ]
})
export class MyApp {
  private readonly api = resolve(ApiClient);
  private readonly aiohaService = resolve(AiohaService);
  private readonly router = resolve(IRouter);
  private readonly routerEvents = resolve(IRouterEvents);

  private removeSessionListener: (() => void) | null = null;
  private removeOpenLoginModalListener: (() => void) | null = null;
  private removeThemeListener: (() => void) | null = null;
  private removeErrorListener: (() => void) | null = null;
  private removeKeyboardListener: (() => void) | null = null;
  private removeVisibilityListener: (() => void) | null = null;
  private routeSubscription: IDisposable | null = null;
  private creditsPollTimer: ReturnType<typeof setInterval> | null = null;
  private creditRefreshInFlight = false;
  private walletLogoutInFlight: Promise<void> | null = null;
  private lastFocusedElement: HTMLElement | null = null;
  private lastCommandPaletteTrigger: HTMLElement | null = null;
  private commandPaletteLoadedForAccount = '';

  private outsideAccountMenuHandler = (e: MouseEvent) => {
    const wrap = document.querySelector('.account-menu-wrap');
    if (wrap && !wrap.contains(e.target as Node)) {
      this.accountMenuOpen = false;
    }
  };

  public isAuthenticated = false;
  public isDarkMode = false;
  public userAccount = '';
  public creditBalance: number | null = null;
  public loginModalOpen = false;
  public authUsernameInput?: HTMLInputElement;
  public authUsername = '';
  public authProvidersLoading = false;
  public authBusy = false;
  public authError = '';
  public currentRoute = '';
  public globalError = '';
  public accountMenuOpen = false;
  public commandAutomations: CommandAutomation[] = [];
  public commandPaletteOpen = false;
  public commandSearch = '';
  public commandPaletteBusy = false;
  public commandPaletteError = '';
  public commandPaletteInput?: HTMLInputElement;
  public commandActiveIndex = 0;

  public get isEditorRoute(): boolean {
    return this.currentRoute === 'automations-edit';
  }

  public get isLowBalance(): boolean {
    return typeof this.creditBalance === 'number' && this.creditBalance < 100;
  }

  public get navItems(): ShellNavItem[] {
    return APP_NAV_ITEMS;
  }

  public get currentRouteLabel(): string {
    return ROUTE_LABELS[this.currentRoute] ?? 'Workspace';
  }

  public get commandShortcutLabel(): string {
    const platform = (window.navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
      ?? window.navigator.platform;
    return /Mac|iPhone|iPad/i.test(platform) ? 'Cmd K' : 'Ctrl K';
  }

  public get commandPalettePlaceholder(): string {
    return this.isAuthenticated
      ? 'Search pages, shortcuts, and automations'
      : 'Search pages and account actions';
  }

  public get commandSections(): Array<{ label: string; items: ShellCommandResult[] }> {
    const query = this.commandSearch.trim().toLowerCase();
    const items = this.getShellCommands()
      .filter((item) => {
        if (!query) {
          return true;
        }

        return [item.title, item.description, item.keywords].some((value) => value.toLowerCase().includes(query));
      })
      .slice(0, 12);

    const order = ['Jump', 'Create', 'Automations', 'Account'];
    let index = 0;
    // Index follows display order so arrow keys move down the list as shown.
    return order
      .map((label) => ({
        label,
        items: items
          .filter((item) => item.group === label)
          .map((item): ShellCommandResult => ({ ...item, index: index++ }))
      }))
      .filter((group) => group.items.length > 0);
  }

  public get commandResults(): ShellCommandResult[] {
    return this.commandSections.flatMap((group) => group.items);
  }

  public binding() {
    this.initTheme();
    this.syncSession();
    this.removeSessionListener = this.api.onSessionChange((session) => {
      this.syncSession();

      if (!session) {
        void loadFromRoot(this.router, '/login');
      }
    });

    this.routeSubscription = this.routerEvents.subscribe('au:router:navigation-end', (event) => {
      const first = event.finalInstructions.children[0];
      if (first) {
        const handler = first.recognizedRoute?.route?.endpoint?.route?.handler as RouteConfig | undefined;
        if (handler?.id) {
          this.currentRoute = handler.id;
        }
      }

      this.closeAccountMenu();
      this.closeCommandPalette(false);

      // One balance check per navigation. ApiClient caches the summary for a
      // few seconds, so a page that loads it too (overview, billing) shares
      // the same request.
      if (this.isAuthenticated) {
        void this.refreshCreditBalance({ silent: true });
      }
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.isAuthenticated) {
        void this.refreshCreditBalance({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.removeVisibilityListener = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    const openLoginModalHandler = () => {
      this.openLoginModal();
    };

    window.addEventListener(OPEN_LOGIN_MODAL_EVENT, openLoginModalHandler);
    this.removeOpenLoginModalListener = () => {
      window.removeEventListener(OPEN_LOGIN_MODAL_EVENT, openLoginModalHandler);
    };

    const onError = (event: ErrorEvent) => {
      this.globalError = event.message || 'An unexpected error occurred.';
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.globalError = reason instanceof Error ? reason.message : String(reason || 'An unexpected error occurred.');
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    this.removeErrorListener = () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };

    const onKeydown = (event: KeyboardEvent) => {
      const isCommandShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (isCommandShortcut) {
        event.preventDefault();
        if (this.commandPaletteOpen) {
          this.closeCommandPalette();
        } else {
          void this.openCommandPalette();
        }
        return;
      }

      if (event.key !== 'Escape') {
        return;
      }

      if (this.commandPaletteOpen) {
        this.closeCommandPalette();
        return;
      }

      if (this.loginModalOpen && !this.authBusy) {
        this.closeLoginModal();
        return;
      }

      if (this.accountMenuOpen) {
        this.closeAccountMenu();
        return;
      }

      if (this.globalError) {
        this.dismissGlobalError();
      }
    };

    window.addEventListener('keydown', onKeydown);
    this.removeKeyboardListener = () => {
      window.removeEventListener('keydown', onKeydown);
    };
  }

  public unbinding() {
    this.removeSessionListener?.();
    this.removeSessionListener = null;
    this.removeOpenLoginModalListener?.();
    this.removeOpenLoginModalListener = null;
    this.removeThemeListener?.();
    this.removeThemeListener = null;
    this.removeErrorListener?.();
    this.removeErrorListener = null;
    this.removeKeyboardListener?.();
    this.removeKeyboardListener = null;
    this.removeVisibilityListener?.();
    this.removeVisibilityListener = null;
    this.routeSubscription?.dispose();
    this.routeSubscription = null;
    this.stopCreditBalancePolling();
    document.removeEventListener('mousedown', this.outsideAccountMenuHandler);
  }

  public async logout() {
    this.closeCommandPalette(false);

    try {
      await this.clearWalletAuth();
    } catch {
      // Wallet sign-out is best effort. It must never keep the Hivett session
      // (and its API token) alive.
    }

    try {
      // Revokes the token server side and always clears the local session,
      // even when the API call fails.
      await this.api.logout();
    } catch {
      // The local session is already cleared by ApiClient.logout().
    }

    await loadFromRoot(this.router, '/login');
  }

  public get hasKeychain() {
    return this.aiohaService.isKeychainAvailable;
  }

  public get hiveSignerAvailable() {
    return this.aiohaService.isHiveSignerAvailable;
  }

  public navigate(path: string, event?: Event) {
    this.closeAccountMenu();
    this.closeCommandPalette(false);
    void navigateFromRoot(this.router, path, event);
  }

  public isNavItemActive(item: ShellNavItem): boolean {
    return item.matches(this.currentRoute);
  }

  public async openNewAutomation() {
    await loadFromRoot(this.router, '/automation/new');
  }

  public async openTemplateLibraryFromShell() {
    window.sessionStorage.setItem(OPEN_TEMPLATE_LIBRARY_STORAGE_KEY, '1');
    await loadFromRoot(this.router, '/automations');
  }

  public async openLoginModal() {
    this.closeCommandPalette(false);
    this.closeAccountMenu();
    this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.authError = '';
    this.authBusy = false;
    this.loginModalOpen = true;
    this.authProvidersLoading = true;

    requestAnimationFrame(() => {
      this.authUsernameInput?.focus();
      this.authUsernameInput?.select();
    });

    try {
      await this.aiohaService.ensureReady();
    } catch (error) {
      this.authError = error instanceof Error ? error.message : 'Wallet providers could not be loaded.';
    } finally {
      this.authProvidersLoading = false;
    }
  }

  public toggleAccountMenu() {
    this.accountMenuOpen = !this.accountMenuOpen;
    if (this.accountMenuOpen) {
      setTimeout(() => document.addEventListener('mousedown', this.outsideAccountMenuHandler), 0);
    } else {
      document.removeEventListener('mousedown', this.outsideAccountMenuHandler);
    }
  }

  public closeAccountMenu() {
    this.accountMenuOpen = false;
    document.removeEventListener('mousedown', this.outsideAccountMenuHandler);
  }

  public toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme();
    localStorage.setItem(THEME_STORAGE_KEY, this.isDarkMode ? 'dark' : 'light');
  }

  public closeLoginModal(restoreFocus: boolean = true) {
    this.authBusy = false;
    this.authProvidersLoading = false;
    this.authError = '';
    this.loginModalOpen = false;

    if (restoreFocus) {
      const focusTarget = this.lastFocusedElement;
      this.lastFocusedElement = null;
      if (focusTarget) {
        requestAnimationFrame(() => focusTarget.focus());
      }
      return;
    }

    this.lastFocusedElement = null;
  }

  public async signInWithKeychain() {
    await this.signInWithProvider('keychain');
  }

  public async signInWithHiveSigner() {
    await this.signInWithProvider('hivesigner');
  }

  public async openCommandPalette() {
    this.closeAccountMenu();
    this.lastCommandPaletteTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.commandPaletteOpen = true;
    this.commandPaletteError = '';
    this.commandSearch = '';
    this.commandActiveIndex = 0;

    requestAnimationFrame(() => {
      this.commandPaletteInput?.focus();
      this.commandPaletteInput?.select();
    });

    await this.ensureCommandPaletteData();
  }

  public async openCommandMenuFromAccountMenu() {
    this.closeAccountMenu();
    await this.openCommandPalette();
  }

  public onCommandSearchInput() {
    this.commandActiveIndex = 0;
    return true;
  }

  public onCommandPaletteKeydown(event: KeyboardEvent): boolean {
    const results = this.commandResults;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (results.length > 0) {
          this.commandActiveIndex = (this.commandActiveIndex + 1) % results.length;
          this.scrollActiveCommandIntoView();
        }
        return false;
      case 'ArrowUp':
        event.preventDefault();
        if (results.length > 0) {
          this.commandActiveIndex = (this.commandActiveIndex - 1 + results.length) % results.length;
          this.scrollActiveCommandIntoView();
        }
        return false;
      case 'Home':
        event.preventDefault();
        this.commandActiveIndex = 0;
        this.scrollActiveCommandIntoView();
        return false;
      case 'End':
        event.preventDefault();
        this.commandActiveIndex = Math.max(0, results.length - 1);
        this.scrollActiveCommandIntoView();
        return false;
      case 'Enter': {
        event.preventDefault();
        const item = results[Math.min(this.commandActiveIndex, results.length - 1)];
        if (item) {
          void this.activateCommand(item);
        }
        return false;
      }
      default:
        return true;
    }
  }

  private scrollActiveCommandIntoView() {
    requestAnimationFrame(() => {
      document
        .querySelector('.command-palette__result.is-active')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }

  public closeCommandPalette(restoreFocus: boolean = true) {
    this.commandPaletteOpen = false;
    this.commandPaletteBusy = false;
    this.commandSearch = '';
    this.commandPaletteError = '';
    this.commandActiveIndex = 0;

    if (restoreFocus) {
      const focusTarget = this.lastCommandPaletteTrigger;
      this.lastCommandPaletteTrigger = null;
      if (focusTarget) {
        requestAnimationFrame(() => focusTarget.focus());
      }
      return;
    }

    this.lastCommandPaletteTrigger = null;
  }

  public async activateCommand(item: ShellCommandItem) {
    this.closeCommandPalette(false);

    if (item.kind === 'route' && item.path) {
      await loadFromRoot(this.router, item.path);
      return;
    }

    if (item.kind === 'automation' && item.automationId) {
      await loadFromRoot(this.router, `/automation/${item.automationId}`);
      return;
    }

    switch (item.actionId) {
      case 'open-login':
        await this.openLoginModal();
        return;
      case 'toggle-theme':
        this.toggleTheme();
        return;
      case 'new-automation':
        await this.openNewAutomation();
        return;
      case 'open-templates':
        await this.openTemplateLibraryFromShell();
        return;
      case 'logout':
        await this.logout();
        return;
    }
  }

  public dismissGlobalError() {
    this.globalError = '';
  }

  public reloadPage() {
    window.location.reload();
  }

  public formatCredits(value: number | null): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '--';
    }

    return new Intl.NumberFormat().format(Math.max(0, Math.floor(value)));
  }

  private startCreditBalancePolling() {
    this.stopCreditBalancePolling();
    this.creditsPollTimer = setInterval(() => {
      // Hidden tabs skip the poll; visibilitychange refreshes on return.
      if (document.visibilityState !== 'visible') {
        return;
      }

      void this.refreshCreditBalance({ silent: true });
    }, CREDIT_BALANCE_REFRESH_MS);
  }

  private stopCreditBalancePolling() {
    if (!this.creditsPollTimer) {
      return;
    }

    clearInterval(this.creditsPollTimer);
    this.creditsPollTimer = null;
  }

  private async refreshCreditBalance(options: { silent?: boolean } = {}) {
    if (!this.isAuthenticated || this.creditRefreshInFlight) {
      return;
    }

    this.creditRefreshInFlight = true;

    try {
      const summary = await this.api.getBillingSummary();
      const nextBalance = Number(summary.credits?.balanceCredits ?? 0);
      this.creditBalance = Number.isFinite(nextBalance) ? Math.max(0, Math.floor(nextBalance)) : 0;
    } catch {
      if (!options.silent) {
        this.creditBalance = null;
      }
    } finally {
      this.creditRefreshInFlight = false;
    }
  }

  private initTheme() {
    const persisted = localStorage.getItem(THEME_STORAGE_KEY);
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    if (persisted === 'dark' || persisted === 'light') {
      this.isDarkMode = persisted === 'dark';
    } else {
      this.isDarkMode = media.matches;
    }

    this.applyTheme();

    const onChange = (event: MediaQueryListEvent) => {
      const storedPreference = localStorage.getItem(THEME_STORAGE_KEY);
      if (storedPreference === 'dark' || storedPreference === 'light') {
        return;
      }

      this.isDarkMode = event.matches;
      this.applyTheme();
    };

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      this.removeThemeListener = () => media.removeEventListener('change', onChange);
      return;
    }

    media.addListener(onChange);
    this.removeThemeListener = () => media.removeListener(onChange);
  }

  private applyTheme() {
    document.documentElement.dataset.theme = this.isDarkMode ? 'dark' : 'light';
    document.documentElement.style.colorScheme = this.isDarkMode ? 'dark' : 'light';
  }

  private async signInWithProvider(provider: WalletProvider) {
    this.authError = '';
    const account = this.authUsername.trim().toLowerCase();

    if (!account) {
      this.authError = 'Hive account is required.';
      return;
    }

    if (!(await this.aiohaService.isProviderEnabled(provider))) {
      if (provider === 'keychain') {
        this.authError = 'Hive Keychain is not available in this browser.';
      } else if (provider === 'hivesigner') {
        this.authError = 'HiveSigner login is not available right now.';
      } else {
        this.authError = 'Selected login provider is not available.';
      }
      return;
    }

    this.authBusy = true;

    try {
      if (!this.isAuthenticated && this.aiohaService.isLoggedIn()) {
        await this.clearWalletAuth();
      }

      if (provider === 'hivesigner') {
        const login = await this.aiohaService.login(provider, account);

        if (!login.success) {
          throw new Error(login.error || 'HiveSigner login failed.');
        }

        await this.api.verifyHiveSigner(login.result, login.username);
      } else {
        const challenge = await this.api.challenge(account);
        const login = await this.aiohaService.login(provider, account, challenge.message);

        if (!login.success) {
          throw new Error(login.error || 'Authentication failed.');
        }

        if (login.username.toLowerCase() !== account) {
          throw new Error(`Signed with @${login.username}, expected @${account}.`);
        }

        await this.api.verify(account, challenge.message, login.result);
      }

      this.closeLoginModal(false);
      await loadFromRoot(this.router, '/overview');
    } catch (error) {
      this.authError = error instanceof Error ? error.message : String(error);
    } finally {
      this.authBusy = false;
    }
  }

  private syncSession() {
    const wasAuthenticated = this.isAuthenticated;
    const previousAccount = this.userAccount;
    const session = this.api.getSession();
    this.isAuthenticated = Boolean(session?.token);
    this.userAccount = session?.user?.account ?? '';

    if (previousAccount !== this.userAccount) {
      this.commandPaletteLoadedForAccount = '';
      this.commandAutomations = [];
    }

    if (this.isAuthenticated) {
      this.loginModalOpen = false;
      this.authBusy = false;
      this.authError = '';
      this.authUsername = this.userAccount;

      if (!wasAuthenticated || previousAccount !== this.userAccount) {
        this.startCreditBalancePolling();
        void this.refreshCreditBalance();
      }
      return;
    }

    this.stopCreditBalancePolling();
    this.creditBalance = null;
    this.commandPaletteLoadedForAccount = '';

    if (this.aiohaService.isLoggedIn()) {
      void this.clearWalletAuth();
    }
  }

  private clearWalletAuth(): Promise<void> {
    if (!this.aiohaService.isLoggedIn()) {
      return Promise.resolve();
    }

    if (!this.walletLogoutInFlight) {
      this.walletLogoutInFlight = this.aiohaService.logout().finally(() => {
        this.walletLogoutInFlight = null;
      });
    }

    return this.walletLogoutInFlight;
  }

  private getShellCommands(): ShellCommandItem[] {
    const routeCommands: ShellCommandItem[] = this.isAuthenticated
      ? this.navItems.map((item) => ({
          id: item.id,
          title: item.label,
          description: '',
          group: 'Jump',
          keywords: `${item.label} ${item.path}`,
          kind: 'route',
          path: item.path
        }))
      : [
          {
            id: 'about',
            title: 'About',
            description: 'Read what Hivett is built for.',
            group: 'Jump',
            keywords: 'about product company overview',
            kind: 'route',
            path: '/about'
          },
          {
            id: 'terms',
            title: 'Terms',
            description: 'Review platform terms.',
            group: 'Jump',
            keywords: 'terms legal policy',
            kind: 'route',
            path: '/terms'
          },
          {
            id: 'privacy',
            title: 'Privacy',
            description: 'Review privacy details.',
            group: 'Jump',
            keywords: 'privacy security policy',
            kind: 'route',
            path: '/privacy'
          }
        ];

    const actionCommands: ShellCommandItem[] = this.isAuthenticated
      ? [
          {
            id: 'new-automation',
            title: 'New automation',
            description: 'Start a fresh workflow from scratch.',
            group: 'Create',
            keywords: 'create automation new workflow',
            kind: 'action',
            actionId: 'new-automation'
          },
          {
            id: 'browse-templates',
            title: 'Browse templates',
            description: 'Open the template library inside automations.',
            group: 'Create',
            keywords: 'template library starter recipe',
            kind: 'action',
            actionId: 'open-templates'
          },
          {
            id: 'toggle-theme',
            title: this.isDarkMode ? 'Switch to light mode' : 'Switch to dark mode',
            description: 'Toggle the app theme.',
            group: 'Account',
            keywords: 'theme appearance dark light',
            kind: 'action',
            actionId: 'toggle-theme'
          },
          {
            id: 'logout',
            title: 'Sign out',
            description: 'End the current session.',
            group: 'Account',
            keywords: 'logout sign out account',
            kind: 'action',
            actionId: 'logout'
          }
        ]
      : [
          {
            id: 'open-login',
            title: 'Sign in',
            description: 'Open the Hive sign-in modal.',
            group: 'Account',
            keywords: 'login sign in auth hivesigner keychain',
            kind: 'action',
            actionId: 'open-login'
          },
          {
            id: 'toggle-theme',
            title: this.isDarkMode ? 'Switch to light mode' : 'Switch to dark mode',
            description: 'Toggle the app theme.',
            group: 'Account',
            keywords: 'theme appearance dark light',
            kind: 'action',
            actionId: 'toggle-theme'
          }
        ];

    const automationCommands: ShellCommandItem[] = this.isAuthenticated
      ? this.commandAutomations.map((item) => ({
          id: `automation-${item.id}`,
          title: item.name,
          description: `${item.enabled ? 'Enabled' : 'Disabled'} · ${item.triggerType ?? 'manual'}${item.account ? ` · @${item.account}` : ''}`,
          group: 'Automations',
          keywords: `${item.name} ${item.id} ${item.triggerType ?? ''} ${item.account ?? ''}`,
          kind: 'automation',
          automationId: item.id
        }))
      : [];

    return [...routeCommands, ...actionCommands, ...automationCommands];
  }

  private async ensureCommandPaletteData() {
    if (!this.isAuthenticated) {
      this.commandAutomations = [];
      this.commandPaletteLoadedForAccount = '';
      return;
    }

    // Reload on every open so renamed, new and deleted automations show up.
    // Cached results stay visible while the refresh runs in the background.
    const hasCachedResults = this.commandPaletteLoadedForAccount === this.userAccount
      && this.commandAutomations.length > 0;

    this.commandPaletteBusy = !hasCachedResults;
    this.commandPaletteError = '';

    try {
      const result = await this.api.listAutomations(undefined, 50);
      this.commandAutomations = (((result.items as Array<Record<string, unknown>>) ?? []).map((item): CommandAutomation => ({
        id: String(item.id ?? ''),
        name: String(item.name ?? 'Untitled automation'),
        enabled: Boolean(item.enabled),
        triggerType: typeof item.trigger === 'object' && item.trigger && 'type' in item.trigger
          ? String((item.trigger as { type?: unknown }).type ?? '')
          : undefined,
        account: typeof item.trigger === 'object' && item.trigger && 'account' in item.trigger
          ? String((item.trigger as { account?: unknown }).account ?? '')
          : undefined
      }))).filter((item) => item.id.length > 0);
      this.commandPaletteLoadedForAccount = this.userAccount;
    } catch (error) {
      this.commandPaletteError = error instanceof Error ? error.message : String(error);
    } finally {
      this.commandPaletteBusy = false;
    }
  }
}
